import type { Action } from 'redux';

import normaliseError, { type NormaliseErrorInput } from '/shared/functions/normaliseError';
import type { NormalisedError } from '/index';
import type { GetApiB2bApplicationByIdFormStateResponses, GetApiCountryByCountryCodeResponse } from '/api/types.gen';

type FormStateResponse = GetApiB2bApplicationByIdFormStateResponses[200];
type FullFormStateResponse = Extract<FormStateResponse, { state: object }>;
type UserDefinedInitiative = FullFormStateResponse['userDefinedInitiatives'][number];
type CoreInitiative = GetApiCountryByCountryCodeResponse['core_initiatives'][number];

export interface InitiativesState {
  items: CoreInitiative[] | null;
  userItems: UserDefinedInitiative[] | null;
  fetching: boolean;
  fetched: boolean;
  error: NormalisedError | null;
}

interface GetUserDefinedInitiativesStartedAction extends Action {
  type: 'GET_USER_DEFINED_INITIATIVES_STARTED';
}

interface GetUserDefinedInitiativesResetAction extends Action {
  type: 'GET_USER_DEFINED_INITIATIVES_RESET';
}

interface GetUserDefinedInitiativesFulfilledAction extends Action {
  type: 'GET_USER_DEFINED_INITIATIVES_FULFILLED';
  userDefinedInitiatives: UserDefinedInitiative[];
}

interface GetUserDefinedInitiativesRejectedAction extends Action {
  type: 'GET_USER_DEFINED_INITIATIVES_REJECTED';
  error: NormaliseErrorInput;
}

interface GetCoreInitiativesStartedAction extends Action {
  type: 'GET_CORE_INITIATIVES_STARTED';
}

interface GetCoreInitiativesFulfilledAction extends Action {
  type: 'GET_CORE_INITIATIVES_FULFILLED';
  initiatives: CoreInitiative[];
}

interface GetCoreInitiativesRejectedAction extends Action {
  type: 'GET_CORE_INITIATIVES_REJECTED';
  error: NormaliseErrorInput;
}

type InitiativesAction =
  | GetUserDefinedInitiativesStartedAction
  | GetUserDefinedInitiativesResetAction
  | GetUserDefinedInitiativesFulfilledAction
  | GetUserDefinedInitiativesRejectedAction
  | GetCoreInitiativesStartedAction
  | GetCoreInitiativesFulfilledAction
  | GetCoreInitiativesRejectedAction;

const defaultState: InitiativesState = {
  items: null,
  userItems: null,
  fetching: false,
  fetched: false,
  error: null,
};

function reducer(state: InitiativesState = defaultState, action: InitiativesAction): InitiativesState {
  switch (action.type) {
    case 'GET_USER_DEFINED_INITIATIVES_STARTED': {
      return {
        ...state,
        fetching: true,
        error: null,
      };
    }
    case 'GET_USER_DEFINED_INITIATIVES_RESET': {
      return {
        ...state,
        fetching: false,
        error: null,
      };
    }
    case 'GET_USER_DEFINED_INITIATIVES_FULFILLED': {
      if (!action.userDefinedInitiatives) {
        return state;
      }
      return {
        ...state,
        fetching: false,
        fetched: true,
        userItems: action.userDefinedInitiatives,
      };
    }
    case 'GET_USER_DEFINED_INITIATIVES_REJECTED': {
      return {
        ...state,
        fetching: false,
        fetched: true,
        error: normaliseError(action.error),
      };
    }
    case 'GET_CORE_INITIATIVES_STARTED': {
      return {
        ...state,
        userItems: null,
        items: null,
        fetching: true,
        error: null,
      };
    }
    case 'GET_CORE_INITIATIVES_FULFILLED': {
      if (!action.initiatives) {
        return state;
      }
      return {
        ...state,
        fetching: false,
        fetched: true,
        items: action.initiatives,
      };
    }
    case 'GET_CORE_INITIATIVES_REJECTED': {
      return {
        ...state,
        fetching: false,
        fetched: true,
        error: normaliseError(action.error),
      };
    }
    default: {
      return state;
    }
  }
}

export default reducer;
